import { __decorate } from "tslib";
import React, { Children, isValidElement } from 'react';
import { action, computed, get, observable, runInAction, set } from 'mobx';
import sortBy from 'lodash/sortBy';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';
import isNumber from 'lodash/isNumber';
import measureScrollbar from '../../../es/_util/measureScrollbar';
import { isCalcSize, toPx } from '../../../es/_util/UnitConvertor';
import { getConfig, getProPrefixCls } from '../../../es/configure';
import Icon from '../../../es/icon';
import isFunction from 'lodash/isFunction';
import Column, { columnWidth } from './Column';
import CustomizationSettings from './customization-settings/CustomizationSettings';
import ObserverCheckBox from '../check-box';
import ObserverRadio from '../radio';
import { DataSetSelection } from '../data-set/enum';
import { ColumnAlign, ColumnLock, DragColumnAlign, SelectionMode, TableAutoHeightType, TableColumnTooltip, TableEditMode, TableHeightType, TableMode, } from './enum';
import { stopPropagation } from '../_util/EventManager';
import { getColumnKey, getColumnLock, getHeader } from './utils';
import getReactNodeText from '../_util/getReactNodeText';
import ColumnGroups from './ColumnGroups';
import autobind from '../_util/autobind';
import { Size } from '../core/enum';
import { $l } from '../locale-context';
import CustomizationColumnHeader from './customization-settings/CustomizationColumnHeader';
export const SELECTION_KEY = '__selection-column__';
export const ROW_NUMBER_KEY = '__row-number-column__';
export const DRAG_KEY = '__drag-column__';
export const EXPAND_KEY = '__expand-column__';
export const CUSTOMIZED_KEY = '__customized-column__';
function columnFilter(column) {
    return Boolean(column);
}
export const getIdList = (startId, endId) => {
    const idList = [];
    const min = Math.min(startId, endId);
    const max = Math.max(startId, endId);
    for (let i = min; i <= max; i++) {
        idList.push(i);
    }
    return idList;
};
function getRowNumbers(record, dataSet, isTree) {
    if (record && dataSet) {
        const { paging, currentPage, pageSize } = dataSet;
        const pageIndex = (isTree ? paging === 'server' : paging) ? (currentPage - 1) * pageSize : 0;
        if (isTree) {
            return record.path.map((r, index) => r.indexInParent + 1 + (index === 0 ? pageIndex : 0));
        }
        return [record.index + 1 + pageIndex];
    }
    return [0];
}
function renderSelectionBox({ record, store }) {
    const { dataSet } = record;
    if (dataSet) {
        const { selection } = dataSet;
        const handleChange = value => {
            if (store.props.selectionMode === SelectionMode.mousedown) {
                // 将处理逻辑交给 mousedown 的处理逻辑 不然会两次触发导致不被勾选上
                return;
            }
            if (value) {
                dataSet.select(record);
            }
            else {
                dataSet.unSelect(record);
            }
        };
        const handleClick = e => {
            stopPropagation(e);
            if (record.isSelected) {
                dataSet.unSelect(record);
            }
        };
        const handleMouseDown = () => {
            if (store.useMouseBatchChoose) {
                store.mouseBatchChooseStartId = record.id;
                store.mouseBatchChooseEndId = record.id;
                store.mouseBatchChooseState = true;
            }
        };
        const handleMouseEnter = () => {
            if (store.useMouseBatchChoose && store.mouseBatchChooseState) {
                store.mouseBatchChooseEndId = record.id;
                store.changeMouseBatchChooseIdList(getIdList(store.mouseBatchChooseStartId, store.mouseBatchChooseEndId));
            }
        };
        if (selection === DataSetSelection.multiple) {
            return (React.createElement(ObserverCheckBox, { checked: record.isSelected, onChange: handleChange, onClick: stopPropagation, onMouseDown: handleMouseDown, onMouseEnter: handleMouseEnter, disabled: !record.selectable, "data-selection-key": SELECTION_KEY, value: true }));
        }
        if (selection === DataSetSelection.single) {
            return (React.createElement(ObserverRadio, { checked: record.isSelected, onChange: handleChange, onClick: handleClick, onMouseDown: handleMouseDown, onMouseEnter: handleMouseEnter, disabled: !record.selectable, "data-selection-key": SELECTION_KEY, value: true }));
        }
    }
}
export function mergeDefaultProps(originalColumns, customizedColumns, parent = null, defaultKey = [0]) {
    const columns = [];
    const leftColumns = [];
    const rightColumns = [];
    const columnSort = {
        left: 0,
        center: 0,
        right: 0,
    };
    originalColumns.forEach((column, index) => {
        if (isPlainObject(column)) {
            const newColumn = { ...Column.defaultProps, ...column };
            if (isNil(getColumnKey(newColumn))) {
                newColumn.key = `anonymous-${defaultKey[0]++}`;
            }
            if (customizedColumns) {
                Object.assign(newColumn, customizedColumns[getColumnKey(newColumn).toString()]);
            }
            if (parent) {
                newColumn.lock = parent.lock;
            }
            if (newColumn.sort === undefined) {
                if (parent) {
                    newColumn.sort = index;
                }
                else {
                    newColumn.sort = columnSort[getColumnLock(newColumn.lock) || 'center']++;
                }
            }
            const { children } = newColumn;
            if (children) {
                newColumn.children = mergeDefaultProps(children, customizedColumns, newColumn, defaultKey);
            }
            if (parent || !newColumn.lock) {
                columns.push(newColumn);
            }
            else if (newColumn.lock === true || newColumn.lock === ColumnLock.left) {
                leftColumns.push(newColumn);
            }
            else {
                rightColumns.push(newColumn);
            }
        }
    }, []);
    if (parent) {
        return sortBy(columns, ({ sort }) => sort);
    }
    return [
        ...sortBy(leftColumns, ({ sort }) => sort),
        ...sortBy(columns, ({ sort }) => sort),
        ...sortBy(rightColumns, ({ sort }) => sort),
    ];
}
export function normalizeColumns(elements, customizedColumns, parent = null, defaultKey = [0]) {
    const columns = [];
    const leftColumns = [];
    const rightColumns = [];
    const columnSort = {
        left: 0,
        center: 0,
        right: 0,
    };
    Children.forEach(elements, (element, index) => {
        if (!isValidElement(element) || !element.type.__PRO_TABLE_COLUMN) {
            return;
        }
        const { props, key } = element;
        const column = {
            ...props,
        };
        if (key) {
            column.key = key;
        }
        else if (isNil(getColumnKey(column))) {
            column.key = `anonymous-${defaultKey[0]++}`;
        }
        if (customizedColumns) {
            Object.assign(column, customizedColumns[getColumnKey(column).toString()]);
        }
        if (parent) {
            column.lock = parent.lock;
        }
        if (column.sort === undefined) {
            if (parent) {
                column.sort = index;
            }
            else {
                column.sort = columnSort[getColumnLock(column.lock) || 'center']++;
            }
        }
        column.children = normalizeColumns(column.children, customizedColumns, column, defaultKey);
        if (parent || !column.lock) {
            columns.push(column);
        }
        else if (column.lock === true || column.lock === ColumnLock.left) {
            leftColumns.push(column);
        }
        else {
            rightColumns.push(column);
        }
    });
    if (parent) {
        return sortBy(columns, ({ sort }) => sort);
    }
    return [
        ...sortBy(leftColumns, ({ sort }) => sort),
        ...sortBy(columns, ({ sort }) => sort),
        ...sortBy(rightColumns, ({ sort }) => sort),
    ];
}
async function getHeaderTexts(dataSet, columns, headers = []) {
    const column = columns.shift();
    if (column) {
        headers.push({ name: column.name, label: await getReactNodeText(getHeader(column, dataSet)) });
    }
    if (columns.length) {
        await getHeaderTexts(dataSet, columns, headers);
    }
    return headers;
}
export default class TableStore {
    constructor(node) {
        this.editors = new Map();
        this.mouseBatchChooseStartId = 0;
        this.mouseBatchChooseEndId = 0;
        this.mouseBatchChooseState = false;
        this.inBatchExpansion = false;
        this.handleSelectAllChange = action(value => {
            const { dataSet, filter } = this.props;
            if (value) {
                dataSet.selectAll(filter);
            }
            else {
                dataSet.unSelectAll();
                if (this.showCachedSelection) {
                    dataSet.clearCachedSelected();
                }
            }
        });
        this.saveCustomizedDebounce = debounce(this.saveCustomized, 1000);
        runInAction(() => {
            this.showCachedSelection = false;
            this.lockColumnsHeadRowsHeight = {};
            this.lockColumnsBodyRowsHeight = {};
            this.lockColumnsFootRowsHeight = {};
            this.node = node;
            this.expandedRows = [];
            this.lastScrollTop = 0;
            this.multiLineHeight = [];
            this.rowHighLight = false;
            this.customizedActiveKey = ['columns'];
            this.originalColumns = [];
            this.tempCustomized = { columns: {} };
            this.customized = { columns: {} };
            this.setProps(node.props);
            if (this.customizable) {
                this.loadCustomized().then(() => {
                    this.initColumns();
                });
            }
            else {
                this.initColumns();
            }
        });
    }
    get dataSet() {
        return this.props.dataSet;
    }
    get prefixCls() {
        const { suffixCls, prefixCls } = this.props;
        return getProPrefixCls(suffixCls, prefixCls);
    }
    get customizable() {
        if (this.columnTitleEditable || this.columnDraggable || this.columnHideable) {
            if ('customizable' in this.props) {
                return this.props.customizable;
            }
            return getConfig('tableCustomizable');
        }
        return false;
    }
    get autoHeight() {
        const { autoHeight } = this.props;
        if (autoHeight) {
            const defaultAutoHeight = {
                type: TableAutoHeightType.minHeight,
                diff: getConfig('tableAutoHeightDiff') || 80,
            };
            if (isObject(autoHeight)) {
                return {
                    ...defaultAutoHeight,
                    ...autoHeight,
                };
            }
            return defaultAutoHeight;
        }
        return undefined;
    }
    get heightType() {
        const tempHeightType = get(this.tempCustomized, 'heightType');
        if (tempHeightType !== undefined) {
            return tempHeightType;
        }
        const { heightType } = this.customized;
        if (heightType !== undefined) {
            return heightType;
        }
        return this.originalHeightType;
    }
    get originalHeightType() {
        const { style, autoHeight } = this.props;
        if (autoHeight) {
            return TableHeightType.flex;
        }
        if (style) {
            const { height } = style;
            if (isString(height) && isCalcSize(height)) {
                return TableHeightType.flex;
            }
            if (isNumber(toPx(height))) {
                return TableHeightType.fixed;
            }
        }
        return TableHeightType.auto;
    }
    /**
     * number 矫正虚拟滚动由于样式问题导致的高度不符问题
     */
    get virtualRowHeight() {
        const { virtualRowHeight } = this.props;
        if (virtualRowHeight) {
            return virtualRowHeight;
        }
        return isNumber(this.rowHeight) ? this.rowHeight + 3 : 33;
    }
    get virtual() {
        return this.props.virtual && this.height !== undefined && isNumber(this.virtualRowHeight);
    }
    get virtualHeight() {
        const { virtualRowHeight, data } = this;
        return Math.round(data.length * virtualRowHeight);
    }
    get virtualStartIndex() {
        const { virtualRowHeight, lastScrollTop } = this;
        return Math.max(Math.round((lastScrollTop / virtualRowHeight) - 3), 0);
    }
    get virtualTop() {
        const { virtualRowHeight, virtualStartIndex } = this;
        return virtualStartIndex * virtualRowHeight;
    }
    get virtualData() {
        const { data, height, virtualRowHeight, props: { virtual } } = this;
        if (virtual && height !== undefined && isNumber(virtualRowHeight)) {
            const { lastScrollTop, virtualStartIndex } = this;
            const virtualEndIndex = Math.min(Math.round((lastScrollTop + height) / virtualRowHeight) + 3, data.length);
            return data.slice(virtualStartIndex, virtualEndIndex);
        }
        return data;
    }
    get hidden() {
        return !!this.styledHidden || this.props.hidden;
    }
    get alwaysShowRowBox() {
        if ('alwaysShowRowBox' in this.props) {
            return this.props.alwaysShowRowBox;
        }
        const alwaysShowRowBox = getConfig('tableAlwaysShowRowBox');
        if (typeof alwaysShowRowBox !== 'undefined') {
            return alwaysShowRowBox;
        }
        return false;
    }
    get keyboard() {
        if ('keyboard' in this.props) {
            return this.props.keyboard;
        }
        const keyboard = getConfig('tableKeyboard');
        if (typeof keyboard !== 'undefined') {
            return keyboard;
        }
        return false;
    }
    get columnResizable() {
        if (this.currentEditRecord) {
            return false;
        }
        if ('columnResizable' in this.props) {
            return this.props.columnResizable;
        }
        return getConfig('tableColumnResizable') !== false;
    }
    get columnHideable() {
        if ('columnHideable' in this.props) {
            return this.props.columnHideable;
        }
        return getConfig('tableColumnHideable') !== false;
    }
    /**
     * 表头支持编辑
     */
    get columnTitleEditable() {
        if ('columnTitleEditable' in this.props) {
            return this.props.columnTitleEditable;
        }
        return getConfig('tableColumnTitleEditable') === true;
    }
    get pagination() {
        if ('pagination' in this.props) {
            return this.props.pagination;
        }
        return getConfig('pagination');
    }
    get dragColumnAlign() {
        if ('dragColumnAlign' in this.props) {
            return this.props.dragColumnAlign;
        }
        return getConfig('tableDragColumnAlign');
    }
    get columnDraggable() {
        if ('columnDraggable' in this.props) {
            return this.props.columnDraggable;
        }
        if ('dragColumn' in this.props) {
            return this.props.dragColumn;
        }
        if (getConfig('tableColumnDraggable') === true) {
            return true;
        }
        return getConfig('tableDragColumn') === true;
    }
    get rowDraggable() {
        if (this.isTree) {
            return false;
        }
        if ('rowDraggable' in this.props) {
            return this.props.rowDraggable;
        }
        if ('dragRow' in this.props) {
            return this.props.dragRow;
        }
        if (getConfig('tableRowDraggable') === true) {
            return true;
        }
        return getConfig('tableDragRow') === true;
    }
    get rowHeight() {
        if ('rowHeight' in this.props) {
            return this.props.rowHeight;
        }
        const rowHeight = getConfig('tableRowHeight');
        if (typeof rowHeight !== 'undefined') {
            return rowHeight;
        }
        return 30;
    }
    get autoFootHeight() {
        if ('autoFootHeight' in this.props) {
            return this.props.autoFootHeight;
        }
        return false;
    }
    get emptyText() {
        return getConfig('renderEmpty')('Table');
    }
    get highLightRow() {
        if ('highLightRow' in this.props) {
            return this.props.highLightRow;
        }
        const tableHighLightRow = getConfig('tableHighLightRow');
        if (tableHighLightRow === false) {
            return false;
        }
        return tableHighLightRow;
    }
    get parityRow() {
        if ('parityRow' in this.props) {
            return this.props.parityRow;
        }
        return getConfig('tableParityRow') === true;
    }
    get autoFocus() {
        if ('autoFocus' in this.props) {
            return this.props.autoFocus;
        }
        return getConfig('tableAutoFocus') !== false;
    }
    get selectedHighLightRow() {
        if ('selectedHighLightRow' in this.props) {
            return this.props.selectedHighLightRow;
        }
        return getConfig('tableSelectedHighLightRow') !== false;
    }
    get editorNextKeyEnterDown() {
        if ('editorNextKeyEnterDown' in this.props) {
            return this.props.editorNextKeyEnterDown;
        }
        return getConfig('tableEditorNextKeyEnterDown') !== false;
    }
    get border() {
        if ('border' in this.props) {
            return this.props.border;
        }
        return getConfig('tableBorder') !== false;
    }
    get queryBar() {
        return this.props.queryBar || getConfig('queryBar');
    }
    get expandIcon() {
        return this.props.expandIcon || getConfig('tableExpandIcon');
    }
    get pristine() {
        return this.props.pristine;
    }
    get currentEditRecord() {
        return this.dataSet.find(record => record.editing === true);
    }
    set currentEditRecord(record) {
        runInAction(() => {
            const { currentEditRecord, dataSet } = this;
            if (currentEditRecord) {
                if (currentEditRecord.isNew) {
                    dataSet.remove(currentEditRecord);
                }
                else {
                    currentEditRecord.reset();
                    currentEditRecord.editing = false;
                }
            }
            if (record) {
                record.editing = true;
            }
        });
    }
    get isTree() {
        return this.props.mode === TableMode.tree;
    }
    get editing() {
        return this.currentEditorName !== undefined || this.currentEditRecord !== undefined;
    }
    get hasRowBox() {
        const { dataSet, selectionMode } = this.props;
        const { alwaysShowRowBox } = this;
        if (dataSet) {
            const { selection } = dataSet;
            return selection && (selectionMode === SelectionMode.rowbox || alwaysShowRowBox);
        }
        return false;
    }
    get useMouseBatchChoose() {
        const { useMouseBatchChoose } = this.props;
        if (useMouseBatchChoose !== undefined) {
            return useMouseBatchChoose;
        }
        if (getConfig('tableUseMouseBatchChoose') !== undefined) {
            return getConfig('tableUseMouseBatchChoose');
        }
        return false;
    }
    get showSelectionTips() {
        const { showSelectionTips } = this.props;
        if (showSelectionTips !== undefined) {
            return showSelectionTips;
        }
        if (getConfig('tableShowSelectionTips') !== undefined) {
            return getConfig('tableShowSelectionTips');
        }
        return false;
    }
    get overflowX() {
        if (isNumber(this.width)) {
            return this.totalLeafColumnsWidth > this.width;
        }
        return false;
    }
    get overflowY() {
        const { bodyHeight, height } = this;
        return (bodyHeight !== undefined &&
            height !== undefined &&
            height < bodyHeight + (this.overflowX ? measureScrollbar() : 0));
    }
    customizedColumnHeader() {
        return React.createElement(CustomizationColumnHeader, { onHeaderClick: this.openCustomizationModal });
    }
    get customizedColumn() {
        if (this.customizable && (!this.rowDraggable || this.dragColumnAlign !== DragColumnAlign.right)) {
            return {
                key: CUSTOMIZED_KEY,
                resizable: false,
                titleEditable: false,
                align: ColumnAlign.center,
                width: 30,
                lock: ColumnLock.right,
                header: this.customizedColumnHeader,
                headerClassName: `${this.prefixCls}-customized-column`,
            };
        }
        return undefined;
    }
    get expandColumn() {
        if (this.expandIconAsCell) {
            return {
                key: EXPAND_KEY,
                resizable: false,
                titleEditable: false,
                align: ColumnAlign.center,
                width: 50,
                lock: true,
            };
        }
        return undefined;
    }
    get rowNumberColumn() {
        const { rowNumber } = this.props;
        if (rowNumber) {
            return {
                key: ROW_NUMBER_KEY,
                resizable: false,
                titleEditable: false,
                headerClassName: `${this.prefixCls}-row-number-column`,
                renderer: this.renderRowNumber,
                tooltip: TableColumnTooltip.overflow,
                align: ColumnAlign.center,
                width: 50,
                lock: true,
            };
        }
        return undefined;
    }
    get selectionColumn() {
        if (this.hasRowBox) {
            const { dataSet, prefixCls } = this;
            const selectionColumn = {
                key: SELECTION_KEY,
                resizable: false,
                titleEditable: false,
                className: `${prefixCls}-selection-column`,
                renderer: this.renderSelectionBox,
                align: ColumnAlign.center,
                width: 50,
                lock: true,
            };
            if (dataSet && dataSet.selection === DataSetSelection.multiple) {
                selectionColumn.header = this.multipleSelectionRenderer;
                selectionColumn.footer = this.multipleSelectionRenderer;
            }
            return selectionColumn;
        }
        return undefined;
    }
    get draggableColumn() {
        const { dragColumnAlign, rowDraggable, prefixCls } = this;
        if (dragColumnAlign && rowDraggable) {
            const draggableColumn = {
                key: DRAG_KEY,
                resizable: false,
                titleEditable: false,
                className: `${prefixCls}-drag-column`,
                renderer: this.renderDragBox,
                align: ColumnAlign.center,
                width: 50,
            };
            if (dragColumnAlign === DragColumnAlign.left) {
                draggableColumn.lock = ColumnLock.left;
            }
            if (dragColumnAlign === DragColumnAlign.right) {
                draggableColumn.lock = ColumnLock.right;
                draggableColumn.header = this.customizable && this.customizedColumnHeader;
            }
            return draggableColumn;
        }
        return undefined;
    }
    get columns() {
        const { dragColumnAlign, originalColumns, expandColumn, draggableColumn, rowNumberColumn, selectionColumn, customizedColumn } = this;
        return observable.array([
            expandColumn,
            dragColumnAlign === DragColumnAlign.left ? draggableColumn : undefined,
            rowNumberColumn,
            selectionColumn,
            ...originalColumns,
            customizedColumn,
            dragColumnAlign === DragColumnAlign.right ? draggableColumn : undefined,
        ]).filter(columnFilter);
    }
    get leftColumns() {
        return this.columns.filter(column => column.lock === ColumnLock.left || column.lock === true);
    }
    get centerColumns() {
        return this.columns.filter(column => !column.lock);
    }
    get rightColumns() {
        return this.columns.filter(column => column.lock === ColumnLock.right);
    }
    get columnGroups() {
        return new ColumnGroups(this.columns);
    }
    get groupedColumns() {
        return this.columnGroups.columns;
    }
    get leftGroupedColumns() {
        return this.groupedColumns.filter(({ column: { lock } }) => lock === ColumnLock.left || lock === true);
    }
    get centerGroupedColumns() {
        return this.groupedColumns.filter(({ column: { lock } }) => !lock);
    }
    get rightGroupedColumns() {
        return this.groupedColumns.filter(({ column: { lock } }) => lock === ColumnLock.right);
    }
    get leafColumns() {
        return this.getLeafColumns(this.columns);
    }
    get leftLeafColumns() {
        return this.getLeafColumns(this.leftColumns);
    }
    get centerLeafColumns() {
        return this.getLeafColumns(this.centerColumns);
    }
    get rightLeafColumns() {
        return this.getLeafColumns(this.rightColumns);
    }
    get leafNamedColumns() {
        return this.leafColumns.filter(column => !!column.name);
    }
    get totalLeafColumnsWidth() {
        return this.leafColumns.reduce((total, column) => total + columnWidth(column), 0);
    }
    get leftLeafColumnsWidth() {
        return this.leftLeafColumns.reduce((total, column) => total + columnWidth(column), 0);
    }
    get rightLeafColumnsWidth() {
        return this.rightLeafColumns.reduce((total, column) => total + columnWidth(column), 0);
    }
    get hasCheckFieldColumn() {
        const { checkField } = this.dataSet.props;
        return this.leafColumns.some(({ name, editor }) => !!editor && checkField === name);
    }
    get hasFooter() {
        return this.leafColumns.some(column => !!column.footer && column.key !== SELECTION_KEY);
    }
    get isAnyColumnsResizable() {
        return this.leafColumns.some(column => column.resizable === true);
    }
    get isAnyColumnsLock() {
        return this.columns.some(column => !!column.lock);
    }
    get isAnyColumnsLeftLock() {
        return this.columns.some(column => column.lock === ColumnLock.left || column.lock === true);
    }
    get isAnyColumnsRightLock() {
        return this.columns.some(column => column.lock === ColumnLock.right);
    }
    get data() {
        const { filter, pristine } = this.props;
        const { dataSet, isTree, showCachedSelection } = this;
        let data = isTree ? dataSet.treeRecords : dataSet.records;
        if (typeof filter === 'function') {
            data = data.filter(filter);
        }
        if (pristine) {
            data = data.filter(record => !record.isNew);
        }
        if (showCachedSelection) {
            return [...dataSet.cachedSelected, ...data];
        }
        return data;
    }
    get indeterminate() {
        const { dataSet } = this;
        if (dataSet) {
            const selectedLength = dataSet.currentSelected.length;
            return !!selectedLength && selectedLength !== dataSet.records.filter(record => record.selectable).length;
        }
        return false;
    }
    get allChecked() {
        const { dataSet } = this;
        if (dataSet) {
            const selectedLength = dataSet.currentSelected.length;
            return !!selectedLength && selectedLength === dataSet.records.filter(record => record.selectable).length;
        }
        return false;
    }
    get expandIconAsCell() {
        const { expandedRowRenderer, expandIconAsCell } = this.props;
        if (expandIconAsCell !== undefined) {
            return expandIconAsCell;
        }
        return !!expandedRowRenderer && !this.isTree;
    }
    get expandIconColumnIndex() {
        const { expandIconAsCell, dragColumnAlign, rowDraggable, props: { expandIconColumnIndex = 0, rowNumber }, } = this;
        if (expandIconAsCell) {
            return 0;
        }
        return expandIconColumnIndex + [this.hasRowBox, rowNumber, dragColumnAlign && rowDraggable].filter(Boolean).length;
    }
    get inlineEdit() {
        return this.props.editMode === TableEditMode.inline;
    }
    getColumnTooltip(column) {
        const { tooltip } = column;
        if (tooltip) {
            return tooltip;
        }
        return getConfig('tableColumnTooltip');
    }
    getColumnHeaders() {
        const { leafNamedColumns, dataSet } = this;
        return getHeaderTexts(dataSet, leafNamedColumns.slice());
    }
    showEditor(name) {
        this.currentEditorName = name;
    }
    setLastScrollTop(lastScrollTop) {
        this.lastScrollTop = lastScrollTop;
    }
    hideEditor() {
        this.currentEditorName = undefined;
    }
    changeMouseBatchChooseIdList(idList) {
        this.mouseBatchChooseIdList = idList;
    }
    showNextEditor(name, reserve) {
        if (reserve) {
            this.dataSet.pre();
        }
        else {
            this.dataSet.next();
        }
        this.showEditor(name);
    }
    setProps(props) {
        this.props = props;
    }
    updateProps(props) {
        this.setProps(props);
        const { customizedCode } = this.props;
        if (this.customizable && customizedCode !== props.customizedCode) {
            this.loadCustomized().then(() => this.initColumns());
        }
        else {
            this.initColumns();
        }
    }
    initColumns() {
        const { customized, customizable } = this;
        const { columns, children } = this.props;
        const customizedColumns = customizable ? customized.columns : undefined;
        this.originalColumns = columns
            ? mergeDefaultProps(columns, customizedColumns)
            : normalizeColumns(children, customizedColumns);
    }
    isRowExpanded(record) {
        const { isExpanded = this.expandedRows.indexOf(record.key) !== -1 } = record;
        return isExpanded && (!this.isTree || !record.parent || this.isRowExpanded(record.parent));
    }
    /**
     *
     * @param record 想修改的record
     * @param expanded 设置是否展开
     * @param disHandler 设置是否需要触发展开事件
     */
    setRowExpanded(record, expanded, disHandler) {
        record.isExpanded = expanded;
        if (!this.inBatchExpansion) {
            const index = this.expandedRows.indexOf(record.key);
            if (expanded) {
                if (index === -1) {
                    this.expandedRows.push(record.key);
                }
            }
            else if (index !== -1) {
                this.expandedRows.splice(index, 1);
            }
        }
        const { onExpand } = this.props;
        if (onExpand && !disHandler) {
            onExpand(expanded, record);
        }
        if (expanded && this.canTreeLoadData) {
            this.onTreeNodeLoad({ record });
        }
    }
    isRowPending(record) {
        return record.pending === true;
    }
    setRowPending(record, pending) {
        record.pending = pending;
    }
    isRowLoaded(record) {
        return record.childrenLoaded === true;
    }
    setRowLoaded(record, loaded) {
        record.childrenLoaded = loaded;
    }
    isRowHover(record) {
        return this.hoverRow === record;
    }
    isRowClick(record) {
        return this.clickRow === record;
    }
    setRowClicked(record, click) {
        this.clickRow = click ? record : undefined;
    }
    get canTreeLoadData() {
        const { treeLoadData, treeAsync } = this.props;
        return treeAsync || !!treeLoadData;
    }
    get isRowHighLight() {
        return this.rowHighLight || this.highLightRow === true;
    }
    setRowHighLight(highLight) {
        this.rowHighLight = highLight;
    }
    setRowHover(record, hover) {
        this.hoverRow = hover ? record : undefined;
    }
    setMultiLineHeight(multiLineHeight) {
        this.multiLineHeight.push(multiLineHeight);
    }
    expandAll() {
        this.inBatchExpansion = true;
        this.expandedRows = this.dataSet.records.map(record => {
            this.setRowExpanded(record, true);
            return record.key;
        });
        this.inBatchExpansion = false;
    }
    collapseAll() {
        this.inBatchExpansion = true;
        this.dataSet.records.forEach(record => this.setRowExpanded(record, false));
        this.expandedRows = [];
        this.inBatchExpansion = false;
    }
    async onTreeNodeLoad({ record }) {
        const { dataSet, treeLoadData, treeAsync } = this.props;
        const promises = [];
        this.setRowPending(record, true);
        if (treeAsync && dataSet) {
            const { idField, parentField } = dataSet.props;
            if (idField && parentField && record && !record.children) {
                const id = record.get(idField);
                promises.push(dataSet.queryMore(-1, { [parentField]: id }));
            }
        }
        if (treeLoadData) {
            promises.push(treeLoadData({ record, dataSet }));
        }
        try {
            await Promise.all(promises);
            this.setRowLoaded(record, true);
        }
        finally {
            this.setRowPending(record, false);
        }
    }
    getLeafColumns(columns) {
        const leafColumns = [];
        columns.forEach(column => {
            if (!column.children || column.children.length === 0) {
                leafColumns.push(column);
            }
            else {
                leafColumns.push(...this.getLeafColumns(column.children));
            }
        });
        return leafColumns;
    }
    renderSelectionBox({ record }) {
        return renderSelectionBox({ record, store: this });
    }
    renderRowNumber({ record, dataSet }) {
        const { isTree, props: { rowNumber } } = this;
        const numbers = getRowNumbers(record, dataSet, isTree);
        const number = numbers.join('-');
        if (typeof rowNumber === 'function') {
            return rowNumber({ record, dataSet, text: number, pathNumbers: numbers });
        }
        return number;
    }
    renderDragBox({ record }) {
        const { rowDragRender } = this.props;
        if (rowDragRender && isFunction(rowDragRender.renderIcon)) {
            return rowDragRender.renderIcon({ record });
        }
        return React.createElement(Icon, { type: "baseline-drag_indicator" });
    }
    changeCustomizedColumnValue(column, value) {
        const { customized: { columns } } = this;
        set(column, value);
        const columnKey = getColumnKey(column).toString();
        const oldCustomized = get(columns, columnKey);
        set(columns, columnKey, {
            ...oldCustomized,
            ...value,
        });
        this.saveCustomizedDebounce();
    }
    saveCustomized(customized) {
        if (this.customizable) {
            const { customizedCode } = this.props;
            if (customized) {
                this.customized = customized;
            }
            if (customizedCode) {
                const tableCustomizedSave = getConfig('tableCustomizedSave');
                tableCustomizedSave(customizedCode, this.customized);
            }
        }
    }
    ;
    openCustomizationModal(modal) {
        const { customizedCode } = this.props;
        const modalProps = {
            drawer: true,
            size: Size.small,
            children: React.createElement(CustomizationSettings, null),
            bodyStyle: {
                overflow: 'hidden auto',
                padding: 0,
            },
        };
        if (customizedCode) {
            modalProps.okText = $l('Table', 'save_button');
        }
        modal.open(modalProps);
    }
    async loadCustomized() {
        const { customizedCode } = this.props;
        if (this.customizable && customizedCode) {
            const tableCustomizedLoad = getConfig('tableCustomizedLoad');
            runInAction(() => {
                this.loading = true;
            });
            try {
                const customized = await tableCustomizedLoad(customizedCode);
                if (customized) {
                    runInAction(() => {
                        this.customized = { columns: {}, ...customized };
                    });
                }
            }
            finally {
                runInAction(() => {
                    this.loading = false;
                });
            }
        }
    }
    multipleSelectionRenderer() {
        return (React.createElement(ObserverCheckBox, { checked: this.allChecked, indeterminate: this.indeterminate, onChange: this.handleSelectAllChange, value: true }));
    }
}
__decorate([
    observable
], TableStore.prototype, "props", void 0);
__decorate([
    observable
], TableStore.prototype, "customized", void 0);
__decorate([
    observable
], TableStore.prototype, "tempCustomized", void 0);
__decorate([
    observable
], TableStore.prototype, "loading", void 0);
__decorate([
    observable
], TableStore.prototype, "originalColumns", void 0);
__decorate([
    observable
], TableStore.prototype, "bodyHeight", void 0);
__decorate([
    observable
], TableStore.prototype, "width", void 0);
__decorate([
    observable
], TableStore.prototype, "height", void 0);
__decorate([
    observable
], TableStore.prototype, "totalHeight", void 0);
__decorate([
    observable
], TableStore.prototype, "lastScrollTop", void 0);
__decorate([
    observable
], TableStore.prototype, "lockColumnsBodyRowsHeight", void 0);
__decorate([
    observable
], TableStore.prototype, "lockColumnsFootRowsHeight", void 0);
__decorate([
    observable
], TableStore.prototype, "lockColumnsHeadRowsHeight", void 0);
__decorate([
    observable
], TableStore.prototype, "expandedRows", void 0);
__decorate([
    observable
], TableStore.prototype, "isHeaderHover", void 0);
__decorate([
    observable
], TableStore.prototype, "hoverRow", void 0);
__decorate([
    observable
], TableStore.prototype, "clickRow", void 0);
__decorate([
    observable
], TableStore.prototype, "currentEditorName", void 0);
__decorate([
    observable
], TableStore.prototype, "styledHidden", void 0);
__decorate([
    observable
], TableStore.prototype, "rowHighLight", void 0);
__decorate([
    observable
], TableStore.prototype, "customizedActiveKey", void 0);
__decorate([
    observable
], TableStore.prototype, "mouseBatchChooseIdList", void 0);
__decorate([
    observable
], TableStore.prototype, "multiLineHeight", void 0);
__decorate([
    observable
], TableStore.prototype, "columnResizing", void 0);
__decorate([
    computed
], TableStore.prototype, "dataSet", null);
__decorate([
    computed
], TableStore.prototype, "prefixCls", null);
__decorate([
    computed
], TableStore.prototype, "customizable", null);
__decorate([
    computed
], TableStore.prototype, "autoHeight", null);
__decorate([
    computed
], TableStore.prototype, "heightType", null);
__decorate([
    computed
], TableStore.prototype, "originalHeightType", null);
__decorate([
    computed
], TableStore.prototype, "virtualRowHeight", null);
__decorate([
    computed
], TableStore.prototype, "virtual", null);
__decorate([
    computed
], TableStore.prototype, "virtualHeight", null);
__decorate([
    computed
], TableStore.prototype, "virtualStartIndex", null);
__decorate([
    computed
], TableStore.prototype, "virtualTop", null);
__decorate([
    computed
], TableStore.prototype, "virtualData", null);
__decorate([
    computed
], TableStore.prototype, "alwaysShowRowBox", null);
__decorate([
    computed
], TableStore.prototype, "keyboard", null);
__decorate([
    computed
], TableStore.prototype, "columnResizable", null);
__decorate([
    computed
], TableStore.prototype, "columnHideable", null);
__decorate([
    computed
], TableStore.prototype, "columnTitleEditable", null);
__decorate([
    computed
], TableStore.prototype, "pagination", null);
__decorate([
    computed
], TableStore.prototype, "dragColumnAlign", null);
__decorate([
    computed
], TableStore.prototype, "columnDraggable", null);
__decorate([
    computed
], TableStore.prototype, "rowDraggable", null);
__decorate([
    computed
], TableStore.prototype, "rowHeight", null);
__decorate([
    computed
], TableStore.prototype, "autoFootHeight", null);
__decorate([
    computed
], TableStore.prototype, "emptyText", null);
__decorate([
    computed
], TableStore.prototype, "highLightRow", null);
__decorate([
    computed
], TableStore.prototype, "parityRow", null);
__decorate([
    computed
], TableStore.prototype, "autoFocus", null);
__decorate([
    computed
], TableStore.prototype, "selectedHighLightRow", null);
__decorate([
    computed
], TableStore.prototype, "editorNextKeyEnterDown", null);
__decorate([
    computed
], TableStore.prototype, "border", null);
__decorate([
    computed
], TableStore.prototype, "queryBar", null);
__decorate([
    computed
], TableStore.prototype, "expandIcon", null);
__decorate([
    computed
], TableStore.prototype, "currentEditRecord", null);
__decorate([
    observable
], TableStore.prototype, "showCachedSelection", void 0);
__decorate([
    computed
], TableStore.prototype, "editing", null);
__decorate([
    computed
], TableStore.prototype, "hasRowBox", null);
__decorate([
    computed
], TableStore.prototype, "useMouseBatchChoose", null);
__decorate([
    computed
], TableStore.prototype, "showSelectionTips", null);
__decorate([
    computed
], TableStore.prototype, "overflowX", null);
__decorate([
    computed
], TableStore.prototype, "overflowY", null);
__decorate([
    autobind
], TableStore.prototype, "customizedColumnHeader", null);
__decorate([
    computed
], TableStore.prototype, "customizedColumn", null);
__decorate([
    computed
], TableStore.prototype, "expandColumn", null);
__decorate([
    computed
], TableStore.prototype, "rowNumberColumn", null);
__decorate([
    computed
], TableStore.prototype, "selectionColumn", null);
__decorate([
    computed
], TableStore.prototype, "draggableColumn", null);
__decorate([
    computed
], TableStore.prototype, "columns", null);
__decorate([
    computed
], TableStore.prototype, "leftColumns", null);
__decorate([
    computed
], TableStore.prototype, "centerColumns", null);
__decorate([
    computed
], TableStore.prototype, "rightColumns", null);
__decorate([
    computed
], TableStore.prototype, "columnGroups", null);
__decorate([
    computed
], TableStore.prototype, "groupedColumns", null);
__decorate([
    computed
], TableStore.prototype, "leftGroupedColumns", null);
__decorate([
    computed
], TableStore.prototype, "centerGroupedColumns", null);
__decorate([
    computed
], TableStore.prototype, "rightGroupedColumns", null);
__decorate([
    computed
], TableStore.prototype, "leafColumns", null);
__decorate([
    computed
], TableStore.prototype, "leftLeafColumns", null);
__decorate([
    computed
], TableStore.prototype, "centerLeafColumns", null);
__decorate([
    computed
], TableStore.prototype, "rightLeafColumns", null);
__decorate([
    computed
], TableStore.prototype, "leafNamedColumns", null);
__decorate([
    computed
], TableStore.prototype, "totalLeafColumnsWidth", null);
__decorate([
    computed
], TableStore.prototype, "leftLeafColumnsWidth", null);
__decorate([
    computed
], TableStore.prototype, "rightLeafColumnsWidth", null);
__decorate([
    computed
], TableStore.prototype, "hasCheckFieldColumn", null);
__decorate([
    computed
], TableStore.prototype, "hasFooter", null);
__decorate([
    computed
], TableStore.prototype, "isAnyColumnsResizable", null);
__decorate([
    computed
], TableStore.prototype, "isAnyColumnsLock", null);
__decorate([
    computed
], TableStore.prototype, "isAnyColumnsLeftLock", null);
__decorate([
    computed
], TableStore.prototype, "isAnyColumnsRightLock", null);
__decorate([
    computed
], TableStore.prototype, "data", null);
__decorate([
    computed
], TableStore.prototype, "indeterminate", null);
__decorate([
    computed
], TableStore.prototype, "allChecked", null);
__decorate([
    computed
], TableStore.prototype, "expandIconAsCell", null);
__decorate([
    computed
], TableStore.prototype, "expandIconColumnIndex", null);
__decorate([
    computed
], TableStore.prototype, "inlineEdit", null);
__decorate([
    action
], TableStore.prototype, "showEditor", null);
__decorate([
    action
], TableStore.prototype, "setLastScrollTop", null);
__decorate([
    action
], TableStore.prototype, "hideEditor", null);
__decorate([
    action
], TableStore.prototype, "changeMouseBatchChooseIdList", null);
__decorate([
    action
], TableStore.prototype, "setProps", null);
__decorate([
    action
], TableStore.prototype, "updateProps", null);
__decorate([
    action
], TableStore.prototype, "initColumns", null);
__decorate([
    action
], TableStore.prototype, "setRowExpanded", null);
__decorate([
    action
], TableStore.prototype, "setRowPending", null);
__decorate([
    action
], TableStore.prototype, "setRowLoaded", null);
__decorate([
    action
], TableStore.prototype, "setRowClicked", null);
__decorate([
    computed
], TableStore.prototype, "canTreeLoadData", null);
__decorate([
    computed
], TableStore.prototype, "isRowHighLight", null);
__decorate([
    action
], TableStore.prototype, "setRowHighLight", null);
__decorate([
    action
], TableStore.prototype, "setRowHover", null);
__decorate([
    action
], TableStore.prototype, "setMultiLineHeight", null);
__decorate([
    action
], TableStore.prototype, "expandAll", null);
__decorate([
    action
], TableStore.prototype, "collapseAll", null);
__decorate([
    autobind
], TableStore.prototype, "onTreeNodeLoad", null);
__decorate([
    autobind
], TableStore.prototype, "renderSelectionBox", null);
__decorate([
    autobind
], TableStore.prototype, "renderRowNumber", null);
__decorate([
    autobind
], TableStore.prototype, "renderDragBox", null);
__decorate([
    action
], TableStore.prototype, "changeCustomizedColumnValue", null);
__decorate([
    action
], TableStore.prototype, "saveCustomized", null);
__decorate([
    autobind
], TableStore.prototype, "openCustomizationModal", null);
__decorate([
    autobind
], TableStore.prototype, "multipleSelectionRenderer", null);
